import {
    AppointmentType,
    CounselingCenterDto,
    CounselingSystemType,
    CreateAppointmentDto,
    CreateCounselingCenterDto,
    CreateLegalEntityDto,
    LegalEntityDto,
    LookupValueDto,
    ObjectStatus,
    RegionDto,
    RegionType,
    SettingsDto,
    UserDto
} from '@/generated/openapi/api'

import Vuex from "vuex";
import lookupValuesService from '@/services/lookupValuesService';
import settingsService from '@/services/settingsService';
import regionsService from '@/services/regionsService';

import counselingCenterService from '@/services/counselingCenterService';
import legalEntityService from '@/services/legalEntityService';

import IPerson from '@/models/IPerson';
import {isProxy, toRaw} from 'vue';

import {CrudStatus, LoadingState} from '@/utils/loadingUtils';
import IClient from "@/models/IClient";

export const defaultPerson: IPerson = {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    legalEntity: {} as LegalEntityDto,
    counselingCentres: [],
    roles: []
}

export const initialAppointment: CreateAppointmentDto = {
    clients: [], counselingCenterId: "", endTime: "", location: "", startTime: "", type: AppointmentType.Video, advisorId: "", advisorName: ""
}

export const initialLegalEntity: CreateLegalEntityDto = {
    areas: [], // Empty array as default
    name: '',
    address: {
        zipCode: '',
        city: '',
        streetName: '',
        houseNumber: '',
        addressSupplement: '',
        region: RegionType.Vienna,
        areaCode: 'A'
    },
    phone: '',
    phone2: '',
    email: '',
    homepage: '',
    description: '',
    registryNumber: ''
}

export const initialCounselingCentre: CreateCounselingCenterDto = {
    status: ObjectStatus.Disabled,
    area: '',
    name: '',
    address: {
        zipCode: '',
        city: '',
        streetName: '',
        houseNumber: '',
        addressSupplement: '',
        region: RegionType.Vienna,
        areaCode: 'A'
    },
    phone: '',
    phone2: '',
    email: '',
    homepage: '',
    description: '',
    // actions: [],
    // onlineServices: [],
    information: '',
    aboutUs: '',
    appointmentInfo: '',
    openingHours: undefined,
    chatCounselingSystem: CounselingSystemType.None,
    videoCounselingSystem: CounselingSystemType.None,
    mailCounselingSystem: CounselingSystemType.None,
    chatConfig: '',
    videoConfig: '',
    mailConfig: '',
    legalEntityIds: []
}

export interface StoreState {
    loading: LoadingState.success,
    crudState: CrudStatus.none,
    crudStateMessage: '',
    isLoggedIn: boolean,
    currentUser: UserDto,
    token: ''
    user: any,
    clients: IClient[],
    appointments: any[],
    area: string | undefined,
    packageVersion: string,
    tenantSettings: Array<SettingsDto>,
    activeTenant: SettingsDto,
    lookUpValues: Map<string, Array<LookupValueDto>>,
    rawLookUpValuesCourt: Map<string, boolean>, // indicator if lookUpValue has usage: "AT_COURT"
    allLookUpValuesTopic: Map<string, Array<LookupValueDto>>,
    regions: Array<RegionDto>,
    selectedFilters: any,
    counselingCenterSelectedLookupValueIds: string[],
    legalEntities: any[],
    counselingCentres: any[],
    persons: any[],
    selectedRechtstraeger: any,
    selectedBeratungsstelle: any,
    selectedPerson: IPerson
}

export default new Vuex.Store({
    state: {
        loading: LoadingState.success,
        crudState: CrudStatus.none,
        crudStateMessage: '',
        isLoggedIn: false,
        token: sessionStorage.getItem('token') || '',
        currentUser: {} as UserDto,
        user: null,
        clients: [],
        appointments: [],
        area: 'FAMILIEN',
        packageVersion: process.env.PACKAGE_VERSION || '0',
        tenantSettings: [],
        activeTenant: {},
        rawLookUpValuesCourt: new Map(),
        lookUpValues: new Map(),
        allLookUpValuesTopic: new Map(),
        regions: [],
        legalEntites: [],
        selectedFilters: {
            topics: [] as string[],
            // lon, lat
            coords: [] as number[],
            district: [] as string[]
        },
        counselingCenterSelectedLookupValueIds: [] as string[],
        wertelistenMockData: [], // from API
        legalEntities: [], // from API
        counselingCentres: [], // from API
        persons: [], // from API
        selectedRechtstraeger: initialLegalEntity,
        selectedBeratungsstelle: initialCounselingCentre,
        selectedPerson: defaultPerson
    } as StoreState,

    mutations: {
        crudState(state, value) {
            state.crudState = value;
        },
        crudStateMessage(state, value) {
            state.crudStateMessage = value
        },
        setToken(state, token) {
            state.token = token;
            sessionStorage.setItem('token', token);
        },
        setUser(state, user) {
            state.user = user;
        },
        setCurrentUser(state, user) {
            state.currentUser = user;
        },
        setArea(state, area) {
            state.area = area;
        },
        setClients(state, clients) {
            state.clients = clients;
        },
        setAppointments(state, appointments) {
            state.appointments = appointments;
        },
        setIsLoggedIn(state, value) {
            state.isLoggedIn = value;
        },
        setActiveTenant(state, value) {
            state.activeTenant = value;

        },
        loadTenantSettings(state, settings) {
            state.tenantSettings = settings
        },
        setAllLookUpValuesTopic(state, value) {
          state.allLookUpValuesTopic.set('topic', value);
        },
        setLookUpValues(state, value: Map<string, Array<LookupValueDto>>) {
            state.lookUpValues = value
        },
        setRawLookUpValuesCourt(state, value: Map<string, boolean>) {
            state.rawLookUpValuesCourt = value
        },
        setRegions(state, value) {
            state.regions = value
        },
        setSelectedCoords(state, value) {
            state.selectedFilters.coords = value;
        },
        setCounselingCenterSelectedLookupValueIds(state, value: string[]) {
            if (value == undefined) {
                state.counselingCenterSelectedLookupValueIds = []
            } else {
                state.counselingCenterSelectedLookupValueIds = value
            }
        },
        legalEntities(state, data) {
            state.legalEntities = data;
        },
        setCounselingCentres(state, data) {
            state.counselingCentres = data;
        },

        // -- Rechtsträger
        setSelectedLegalEntity(state, legalEntity) {
            state.selectedRechtstraeger = legalEntity;
        },
        updateLegalEntities(state, updatedRechtstraeger) {
            const index = state.legalEntities.findIndex(r => r.id === updatedRechtstraeger.id);
            if (index !== -1) {
                state.legalEntities[index] = updatedRechtstraeger;
            }
        },
        addLegalEntities(state, newRechtstraeger) {
            state.legalEntities.push(newRechtstraeger as LegalEntityDto);
        },
        resetSelectedLegalEntity(state) {
            state.selectedRechtstraeger = {...initialLegalEntity}; // you might need to deep clone if initialLegalEntity contains complex objects
        },
        deleteLegalEntities(state, id) {
            const index = state.legalEntities.findIndex(r => r.id === id);
            if (index !== -1) {
                state.legalEntities.splice(index, 1);
            }
        },

        // -- Beratungsstelle
        setSelectedCounselingCentre(state, counselingCentre) {
            state.selectedBeratungsstelle = counselingCentre;
        },
        updateCounselingCentreMockData(state, updatedBeratungsstelle) {
            const index = state.counselingCentres.findIndex(r => r.id === updatedBeratungsstelle.id);
            if (index !== -1) {
                state.counselingCentres[index] = updatedBeratungsstelle;
            }
        },
        addCounselingCentre(state, newBeratungsstelle) {
            state.counselingCentres.push(newBeratungsstelle as CounselingCenterDto);
        },
        resetSelectedCounselingCentre(state) {
            // eslint-disable-next-line no-console
            state.selectedBeratungsstelle = {...initialCounselingCentre}; // you might need to deep clone if initialLegalEntity contains complex objects
        },
        deleteCounselingCentre(state, id) {
            const index = state.counselingCentres.findIndex(r => r.id === id);
            if (index !== -1) {
                state.counselingCentres.splice(index, 1);
            }
        },

        // -- Person
        setSelectedPerson(state, person) {
            state.selectedPerson = person;
        },
        updatePerson(state, updatedPerson) {
            const index = state.persons.findIndex(r => r.id === updatedPerson.id);
            if (index !== -1) {
                state.persons[index] = updatedPerson;
            }
        },
        addPerson(state, newPerson) {
            state.persons.push(newPerson as IPerson);
        },
        resetSelectedPerson(state) {
            state.selectedPerson = {} as IPerson;
        },
        deletePerson(state, id) {
            const index = state.persons.findIndex(r => r.id === id);
            if (index !== -1) {
                state.persons.splice(index, 1);
            }
        },
    },
    getters: {
        crudState: (state) => {
          return state.crudState;
        },
        crudStateMessage: (state) => {
          return state.crudStateMessage;
        },
        appVersion: (state) => {
            return state.packageVersion
        },
        area: (state) => {
            return state.area
        },
        currentUser: (state) => {
          return state.currentUser
        },
        tenantSettings: (state) => {
            if (isProxy(state.tenantSettings)) {
                return toRaw(state.tenantSettings);
            }
            return state.tenantSettings
        },
        activeTenant: (state) => {
            if (isProxy(state.activeTenant)) {
                return toRaw(state.activeTenant);
            }
            return state.activeTenant
        },
        clients: (state) => {
            let rawData = state.clients;

            if (isProxy(state.clients)) {
                rawData = toRaw(state.clients)
            }

            const rt = rawData.map((item) => item);
            return rt;
        },
        appointments: (state) => {
            let rawData = Array.isArray(state.appointments) ? state.appointments : [];

            if (isProxy(rawData)) {
                rawData = toRaw(rawData);
            }

            // Additional check to ensure rawData is an array
            if (!Array.isArray(rawData)) {
                return []; // Return an empty array if rawData is not an array
            }

            const rt = rawData.map((item) => item);
            return rt;
        },
        selectedRechtstraeger: (state) => {
            let rawData = state.selectedRechtstraeger;

            if (isProxy(state.selectedRechtstraeger)) {
                rawData = toRaw(state.selectedRechtstraeger)
            }

            return rawData;
        },
        legalEntity: (state) => {
            let rawData = state.legalEntities;

            if (isProxy(state.legalEntities)) {
                rawData = toRaw(state.legalEntities)
            }

            const rt = rawData.map((item) => item);
            return rt;
        },
        counselingCentres: (state) => {
            if (isProxy(state.counselingCentres)) {
                return toRaw(state.counselingCentres);
            }
            return state.counselingCentres;
        },
        rechtstraegerById: (state) => (id: string) => {
            let rawData = state.legalEntities;

            if (isProxy(state.legalEntities)) {
                rawData = toRaw(state.legalEntities)
            }

            const rt = rawData.find((item) => item.id === id);

            return rt;
        },
        selectedBeratungsstelle: (state) => {
            let rawData = state.selectedBeratungsstelle;

            if (isProxy(state.selectedBeratungsstelle)) {
                rawData = toRaw(state.selectedBeratungsstelle)
            }

            return rawData;
        },
        beratungsstelleById: (state) => (id: string) => {
            let rawData = state.counselingCentres;

            if (isProxy(state.counselingCentres)) {
                rawData = toRaw(state.counselingCentres)
            }

            const rt = rawData.find((item) => item.id === id);

            return rt;
        },
        selectedPerson: (state) => {
            let rawData = state.selectedPerson;

            if (isProxy(state.selectedPerson)) {
                rawData = toRaw(state.selectedPerson)
            }

            return rawData;
        },
        persons: (state) => {
            let rawData = state.persons;

            if (isProxy(state.persons)) {
                rawData = toRaw(state.persons)
            }

            const rt = rawData.map((item) => item);
            return rt;
        },
        personById: (state) => (id: string) => {
            let rawData = state.persons;

            if (isProxy(state.persons)) {
                rawData = toRaw(state.persons)
            }

            const rt = rawData.find((item) => item.id === id);

            return rt;
        },
    },
    actions: {

        async register({commit}, user) {
            const response = await fetch('/api/auth/register', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(user),
            });

            if (response.ok) {
                const data = await response.json();
                // Optionally, you might want to log the user in immediately after registration,
                // or notify them to check their email to verify their account, etc.
                commit('setUser', data.user);
                commit('setToken', data.token);  // Assuming the token is returned upon successful registration
            } else {
                const errorData = await response.json();
                // handle error, e.g., show error message to the user
                // console.error('Registration failed:', errorData.message);
            }
        },
        async login({commit}, user) {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(user),
            });

            if (response.ok) {
                const data = await response.json();
                commit('setToken', data.token);

                // Fetch user data
                const userResponse = await fetch('/api/user', {
                    headers: {'Authorization': `Bearer ${data.token}`}
                });

                if (userResponse.ok) {
                    const userData = await userResponse.json();
                    commit('setUser', userData);
                } else {
                    // handle error, e.g., show error message to the user
                    // console.error('Failed to fetch user data');
                }
            } else {
                const errorData = await response.json();
                // handle error, e.g., show error message to the user
                // console.error('Login failed:', errorData.message);
            }
        },

        updateIsLoggedIn({commit}, value) {
            commit('setIsLoggedIn', value);
        },

        async loadTenantSettingsIfNotLoaded(context, forceReload?: boolean): Promise<SettingsDto[]> {
            if (forceReload || !(context.state.tenantSettings.length > 0)) {
                const promise = settingsService.getSettings()
                promise.then(resp => {
                    context.commit("loadTenantSettings", resp)

                    return resp;
                }).catch(err => {
                    throw err
                })
                return promise;

            } else {
                return Promise.resolve(context.state.tenantSettings)
            }
        },

        async loadCurrentUserIfNotLoaded(context, forceReload?: boolean): Promise<UserDto> {
            if (forceReload || !(context.state.currentUser.id)) {
                const promise = settingsService.getCurrentUser()
                promise.then(resp => {
                    context.commit("setCurrentUser", resp)

                    if (resp.roles?.includes("ADMIN")) {
                        context.commit("setArea", undefined)
                    } else {
                        context.commit("setArea", resp.area)
                    }

                    return resp
                }).catch(err => {
                    throw err
                })
                return promise;

            } else {
                return Promise.resolve(context.state.currentUser)
            }
        },

        loadAllLookUpValuesIfNotLoaded(context, forceReload?: boolean): Promise<Map<string, Array<LookupValueDto>>> {
            if(forceReload === true) {
            context.commit('setLookUpValues', new Map())
            }
            if (context.state.lookUpValues.size === 0) {
                return (this.dispatch('loadTenantSettingsIfNotLoaded') as Promise<SettingsDto[]>).then(settingsDto => {
                    return lookupValuesService.getLookupValuesByLookupValueType(undefined, this.state.area).then(resp => {
                        const lookupValuesMap = new Map() as Map<string, Array<LookupValueDto>>

                        if (settingsDto && settingsDto.length > 0) {
                            settingsDto.map((e) => e.enabledLookupValueTypes?.forEach((element) => {
                                const filtered = resp.filter(value => {
                                    return value.type === element.toString()
                                })
                                lookupValuesMap.set(element, filtered)
                              })
                            );
                        }
                        context.commit("setLookUpValues", lookupValuesMap)
                        return lookupValuesMap
                    }).catch(err => {
                        throw err
                    })
                })
            } else {
                // lookupValues have already been loaded, just resolve the promise.
                return Promise.resolve(context.state.lookUpValues)
            }
        },

        clearLookupValueStore() {
            return new Promise<void>((resolve, _) => {
                this.commit('setLookUpValues', new Map())
                resolve()
            })
        },

        getAllLookUpValuesTopic(context) {
            lookupValuesService.getLookupValuesTopic().then(resp => {
                context.commit("setAllLookUpValuesTopic", resp)
            }).catch(err => {
                throw err
            })
        },

        getAllRegions(context) {
            regionsService.getRegions().then(resp => {
                context.commit("setRegions", resp)
            }).catch(err => {
                throw err
            })
        },

        getAllLookUpValues(context): Promise<Map<string, Array<LookupValueDto>>> {
            return lookupValuesService.getLookupValuesByLookupValueType().then(resp => {
                const lookupValuesMap = new Map() as Map<string, Array<LookupValueDto>>
                const rawLookUpValuesCourtMap = new Map() as Map<string, boolean>;

                resp.forEach((item) => {
                    if (item.type) {
                        const temp = (lookupValuesMap.get(item.type) || []);
                        temp.push(item)
                        lookupValuesMap.set(item.type, temp)
                    }
                    rawLookUpValuesCourtMap.set(item.id, (item.usage && item.usage == "AT_COURT") ? true : false)
                });

                context.commit("setLookUpValues", lookupValuesMap)
                context.commit("setRawLookUpValuesCourt", rawLookUpValuesCourtMap)

                return lookupValuesMap
            }).catch(err => {
                throw err
            })
        },
        fetchLegalEntities({commit}) {
            legalEntityService.getLegalEntitiesByAdmin(0, 0).then(result => {
                commit('legalEntities', result.content);
            }).catch(() => {
            })
        },
        fetchCounselingCenters({commit}) {
            counselingCenterService.findCounselingCentersByAdmin().then(resp => {
                commit('setCounselingCentres', resp.content)
            });
        },

        // -- Rechtstraeger
        updateSelectedLegalEntity({commit}, legalEntity) {
            commit('setSelectedLegalEntity', legalEntity);
        },
        fetchSelectedLegalEntity({commit, state}, id) {
            let rawData = state.legalEntities;

            if (isProxy(state.legalEntities)) {
                rawData = toRaw(state.legalEntities)
            }

            const data = rawData.find((item) => item.id === id);
            commit('setSelectedLegalEntity', data);
        },
        updateLegalEntities({commit}, legalEntity) {
            commit('updateLegalEntities', legalEntity);
        },
        addLegalEntities({commit}, legalEntity) {
            commit('addLegalEntities', legalEntity);
        },
        resetSelectedLegalEntity({commit}) {
            commit('resetSelectedLegalEntity');
        },
        deleteLegalEntities({commit}, id) {
            commit('deleteLegalEntities', id);
        },

        // -- Beratungsstelle
        updateSelectedCounselingCentre({commit}, counselingCentre) {
            commit('setSelectedCounselingCentre', counselingCentre);
        },
        fetchSelectedCounselingCentre({commit, state}, id) {
            let rawData = state.counselingCentres;

            if (isProxy(state.counselingCentres)) {
                rawData = toRaw(state.counselingCentres)
            }

            const data = rawData.find((item) => item.id === id);
            commit('setSelectedCounselingCentre', data);
        },
        updateCounselingCentreMockData({commit}, counselingCentre) {
            commit('updateCounselingCentreMockData', counselingCentre);
        },
        addCounselingCentre({commit}, counselingCentre) {
            commit('addCounselingCentre', counselingCentre);
        },
        resetSelectedCounselingCentre({commit}) {
            commit('resetSelectedCounselingCentre');
        },
        deleteCounselingCentre({commit}, id) {
            commit('deleteCounselingCentre', id);
        },

        // -- Person
        updateSelectedPerson({commit}, person) {
            commit('setSelectedPerson', person);
        },
        fetchSelectedPerson({commit, state}, id) {
            let rawData = state.persons;

            if (isProxy(state.persons)) {
                rawData = toRaw(state.persons)
            }

            const data = rawData.find((item) => item.id === id);
            commit('setSelectedPerson', data);
        },
        updatePerson({commit}, person) {
            commit('updatePerson', person);
        },
        addPerson({commit}, person) {
            commit('addPerson', person);
        },
        resetSelectedPerson({commit}) {
            commit('resetSelectedPerson');
        },
        deletePerson({commit}, id) {
            commit('deletePerson', id);
        },
    }
})
